import {ThemeOptions} from '@mui/material/styles';
import {createBreakpoints} from "@mui/system";

export const CUSTOM_BREAKPOINTS = {
    values: {
        xs: 0,
        sm: 600,
        md: 828,
        lg: 1056,
        xl: 1392,
    },
}

const breakpoints = createBreakpoints({...CUSTOM_BREAKPOINTS})


const portalTheme: ThemeOptions = {
    breakpoints,
    palette: {
        mode: 'light',
        primary: {
            main: '#4D841F',
            dark: '#275e18',
            light: '#C7DEAE',
            contrastText: "#FFFFFF"
        },
        secondary: {
            main: '#0076B9',
            dark: '#005284',
            light: '#005284',
            contrastText: "#FFFFFF"
        },
        tertiary: {
            main: '#FFDD00',
            contrastText: "#4A3F00",
            dark: '#E1BC06'
        },
        error: {
            main: '#d32f2f',
            dark: '#c62828',
            light: '#ef5350',
            contrastText: '#FFFFFF'
        },
        action: {
            active: '#333333',
            hover: 'rgba(0, 0, 0, 0.04)',
            selected: 'rgba(0, 0, 0, 0.08)'
        },
        background: {
            default: '#ffffff',
            paper: '#ffffff',
        },
        text: {
            primary: '#333333',
            secondary: '#999999'
        },
        contrastThreshold: 4.5,
    },
    spacing: 4,
    typography: {
        fontFamily: '"HelveticaNeueLTStd-Roman", Helvetica, Arial, sans-serif',
        fontSize: 14,
        button: {
            textTransform: 'none'
        },
        h1: {
            fontSize: '3.25rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '-0.09375rem',
            lineHeight: '120%',
            [breakpoints.down('md')]: {
                fontSize: '2.75rem',
                letterSpacing: '-0.0625rem',
            },
            [breakpoints.down('sm')]: {
                fontSize: '2.25rem',
                letterSpacing: '-0.0625rem',
            },
        },
        h2: {
            fontSize: '2.75rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '-0.03125rem',
            lineHeight: '120%',
            [breakpoints.only('md')]: {
                fontSize: '2.25rem',
            },
            [breakpoints.down('md')]: {
                fontSize: '1.75rem',
            },
        },
        h3: {
            fontSize: '2rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '0px',
            lineHeight: '120%',
            [breakpoints.only('md')]: {
                fontSize: '1.75rem',
            },
            [breakpoints.down('md')]: {
                fontSize: '1.5rem',
            },
        },
        h4: {
            fontSize: '1.25rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '0.01563rem',
            lineHeight: '125%',
        },
        h5: {
            fontSize: '1.25rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '0.01563rem',
            lineHeight: '125%',

        },
        h6: {
            fontSize: '1.25rem',
            fontFamily: 'var(--font-family-bold)',
            fontWeight: '700',
            letterSpacing: '0.01563rem',
            lineHeight: '160%',
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: '400',
            letterSpacing: '0.00938rem',
            lineHeight: '175%'
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: '400',
            letterSpacing: '0.1px',
            lineHeight: '157%'
        },
        body1: {
            fontSize: '1.125rem',
            fontWeight: '400',
            letterSpacing: '0.15px',
            lineHeight: '150%',
            [breakpoints.down('md')]: {
                fontSize: '1rem',
            }
        },
        body2: {
            fontSize: '1rem',
            fontWeight: '400',
            letterSpacing: '0.17px',
            lineHeight: '143%'
        },
        caption: {
            fontSize: '0.75rem',
            fontWeight: '400',
            letterSpacing: '0.4px',
            lineHeight: '28px'
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: '450',
            letterSpacing: '0.35px',
            lineHeight: '18px'
        },
        ctaButton: {
            fontSize: '15px',
            fontFamily: '"HelveticaNeueLTStd-Md", Helvetica, Arial, sans-serif',
            lineHeight: '26px',
        },
        navigationItemDense: {
            fontSize: '14px',
            letterSpacing: '0.17px',
            lineHeight: '143%',
            fontFamily: '"HelveticaNeueLTStd-Md", Helvetica, Arial, sans-serif',
            fontWeight: '500',
        },
        accordion: {
            fontSize: '18px',
            lineHeight: '130%',
            fontFamily: '"HelveticaNeueLTStd-Md", Helvetica, Arial, sans-serif',
            fontWeight: '500',
            letterSpacing: '0.15px',
        },
        navigationItem: {
            fontSize: '16px',
            fontStyle: 'normal',
            fontFamily: '"HelveticaNeueLTStd-Md", Helvetica, Arial, sans-serif',
            fontWeight: '500',
            lineHeight: '150%',
            letterSpacing: '0.15px',
        },
        linkLabel: {
            fontFamily: 'var(--font-family-bold)',
            letterSpacing: '0.15px',
            lineHeight: '150%',
            textDecoration: 'none',
            fontSize: "18px",
            [breakpoints.down('md')]: {
                fontSize: '1rem',
            },
            fontWeight: "450",
            '&:hover': {
                textDecoration: 'underline',
                textDecorationThickness: "2px",
                textUnderlineOffset: "4px",

            }

        },
        blockQuote: {
            textAlign: 'center',
            fontSize: '40px',
            fontStyle: 'normal',
            fontWeight: 450,
            lineHeight: '140%',
        }
    },
    components: {
        MuiLink: {
            defaultProps: {
                underline: "none",
                fontSize: {
                    md: "16px",
                    lg: "18px"
                },
                fontWeight: "450"
            },
            styleOverrides: {
                root: () => ({
                    textDecoration: "underline",
                    textDecorationThickness: "2px",
                    textUnderlineOffset: "4px",
                    "&:hover": {
                        textDecoration: "none",
                    }
                }),
            }
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiButton: {
            defaultProps: {
                style: {
                    borderRadius: 999,
                    boxShadow: "none"
                }
            },
            styleOverrides: {
                root: {
                    position: 'relative',
                    '&:focus-visible::after': {
                        content: '""',
                        position: 'absolute',
                        top: -6,
                        left: -6,
                        right: -6,
                        bottom: -6,
                        border: '2px solid var(--focus-state-blue)',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                    },
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    position: 'relative',
                    '& input:focus-visible + svg': {
                        outline: '2px solid var(--focus-state-blue)',
                        outlineOffset: '2px',
                        borderRadius: 2,
                    },
                }
            }
        },
        MuiCard: {
            defaultProps: {
                style: {
                    borderRadius: 0
                }
            }
        },
        MuiAccordion: {
            styleOverrides: {
                root: ({theme}) => ({
                    "&:before": {
                        display: 'none',
                        backgroundColor: "transparent !important",
                    },
                    '& .Mui-expanded .MuiAccordionSummary-content p, .Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                        color: theme.palette.primary.main
                    },

                }),
            },
            defaultProps: {
                style: {
                    borderBottom: "0px solid #333333",
                    borderTop: "1px solid #e0e0e0",
                    borderRadius: "0",
                    boxShadow: "none",
                }
            }
        },
        MuiInput: {
            defaultProps: {
                style: {
                    fontFamily: 'Helvetica Neue LT',
                    fontSize: '1rem',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '150%',
                    letterSpacing: '0.15px',
                }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: '#333333',
                    '&.Mui-focused': {
                        color: 'primary.main',
                    },
                },
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: '#333',
                    borderWidth: '1px',
                    borderStyle: 'solid',

                },
                root: ({theme}) => ({
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#333',
                        borderWidth: '2px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '2px',
                    },
                }),
            },
        },
        MuiListItem: {
            defaultProps: {
                style: {
                    fontSize: '1rem',
                    padding: 2
                }
            }
        },


    },
};

// Adding custom typography variant
declare module '@mui/material/styles' {
    interface TypographyVariants {
        accordion: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        accordion?: React.CSSProperties;
    }

    interface TypographyVariants {
        navigationItemDense: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        navigationItemDense?: React.CSSProperties;
    }

    interface TypographyVariants {
        navigationItem: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        navigationItem?: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        linkLabel: React.CSSProperties | any;
    }

    interface TypographyVariantsOptions {
        blockQuote: React.CSSProperties | any;
    }

    interface TypographyVariants {
        ctaButton: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        ctaButton?: React.CSSProperties;
    }

}

declare module '@mui/material/styles/createPalette' {
    interface Palette {
        tertiary?: {
            main: string;
            dark?: string;
            light?: string;
            contrastText: string;
        };
    }

    interface PaletteOptions {
        tertiary?: {
            main: string;
            dark?: string;
            light?: string;
            contrastText: string;
        };
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        navigationItemDense: true;
        accordion: true;
        navigationItem: true;
        linkLabel: true;
        blockQuote: true;
        ctaButton: true;
    }
}

export default portalTheme;
